/** Pure, read-only source projection. A row break never establishes a verse. */
export type VerseHalf = { verse: number; half: 1 | 2; text: string; start: number; end: number; offsets: number[] }
export type VerseIndex = { normalized: string; halves: VerseHalf[] }
export type VerseFragment = { half: VerseHalf; start: number; end: number; cellSpans: Array<{ halfOffset: number; start: number; end: number }> }
const normalize = (text: string) => text.replace(/[\s/]/gu, '')
const cache = new Map<string, VerseIndex | null>()

export function indexVerseSource(source: string): VerseIndex | null {
  if (cache.has(source)) return cache.get(source)!
  const lines = source.split(/\r?\n/).map(s => s.trim()).filter(s => s && !/^fol\./i.test(s))
  const paired = lines.filter(s => {
    const parts = s.split('/').filter(p => p.trim())
    return /[\u0621-\u064A]/.test(s) && parts.length >= 2 && parts.length % 2 === 0
  })
  let result: VerseIndex | null = null
  if (paired.length >= 3 && paired.length / lines.length >= 0.8) {
    const halves: VerseHalf[] = []
    let normalized = '', verse = 0
    for (const line of lines) {
      const parts = line.split('/').filter(s => s.trim())
      for (let i = 0; i < parts.length; i++) {
        const text = parts[i].trim(), start = normalized.length
        normalized += normalize(text)
        if (!paired.includes(line)) continue
        if (i % 2 === 0) verse++
        const offsets = Array.from(text.matchAll(/[^\s/]/gu), m => m.index!)
        halves.push({ verse, half: i % 2 === 0 ? 1 : 2, text, start, end: normalized.length, offsets })
      }
    }
    result = { normalized, halves }
  }
  if (cache.size >= 64) cache.delete(cache.keys().next().value as string)
  cache.set(source, result)
  return result
}

export function projectVerseCell(index: VerseIndex | null, cellText: string): VerseFragment[] | null {
  const needle = normalize(cellText)
  if (!index || !needle) return null
  const start = index.normalized.indexOf(needle)
  if (start < 0 || index.normalized.indexOf(needle, start + 1) >= 0) return null
  const end = start + needle.length
  // Unsupported astral source characters stay literal rather than mixing
  // code-point indexing with the editor's UTF-16 offsets.
  if (/[\uD800-\uDFFF]/.test(index.normalized + cellText)) return null
  const cellOffsets = Array.from(cellText.matchAll(/[^\s/]/gu), m => m.index!)
  let covered = 0
  const fragments = index.halves.flatMap(half => {
    const a = Math.max(start, half.start), b = Math.min(end, half.end)
    if (a >= b) return []
    covered += b - a
    const cellSpans = Array.from({ length: b - a }, (_, i) => ({
      halfOffset: half.offsets[a - half.start + i],
      start: cellOffsets[a - start + i], end: cellOffsets[a - start + i] + 1,
    }))
    return [{ half, start: half.offsets[a - half.start], end: half.offsets[b - half.start - 1] + 1, cellSpans }]
  })
  return covered === needle.length ? fragments : null
}

/** Display-only kashida with offsets into the unchanged source half. */
export function justifyVerseHalf(text: string, target: number, measure: (s: string) => number) {
  const counts = new Map<number, number>()
  const slots = Array.from(text.matchAll(/([بتثجحخسشصضطظعغفقكلمنهيئ][\u064B-\u065F\u0670]*)(?=[آأإاب-غف-ي])/gu), m => m.index! + m[0].length)
  const build = () => Array.from({ length: text.length }, (_, i) => text[i] + 'ـ'.repeat(counts.get(i + 1) || 0)).join('')
  let stretched = text, width = measure(text)
  for (let round = 0; round < 32; round++) {
    let changed = false
    for (const at of slots) {
      counts.set(at, (counts.get(at) || 0) + 1)
      const candidate = build(), next = measure(candidate)
      if (next > target || next <= width) counts.set(at, counts.get(at)! - 1)
      else { stretched = candidate; width = next; changed = true }
    }
    if (!changed) break
  }
  const offset = (sourceOffset: number) => sourceOffset + Array.from(counts).reduce((sum, [at, count]) => sum + (at <= sourceOffset ? count : 0), 0)
  // -1 identifies ONLY inserted decoration; source-authored tatweel is retained.
  const origins = Array.from({ length: text.length }, (_, i) => [i, ...Array(counts.get(i + 1) || 0).fill(-1)]).flat()
  return { text: stretched, offset, origins }
}
